import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface InvoiceRow {
  readonly invoiceId: string;
  readonly invoiceNumber: string;
  readonly saasTenantName: string | null;
  readonly saasTenantCode: string | null;
  readonly invoiceType: string;
  readonly status: string;
  readonly currency: string;
  readonly totalAmount: string;
  readonly balanceAmount: string;
}

const INVOICE_TYPES = ['TAX_INVOICE', 'PROFORMA', 'RENEWAL', 'CREDIT_NOTE', 'DEBIT_NOTE', 'ADJUSTMENT'];
const INVOICE_STATUSES = ['DRAFT', 'ISSUED', 'PARTIALLY_PAID', 'PAID', 'OVERDUE', 'CANCELLED', 'VOID'];
const PAGE = 25;

/**
 * M44 §11 Invoice Management Screen (§28). Commercial operators generate
 * invoices from billing transactions, review them and drive invoice status.
 * Viewing needs M44_INVOICE_VIEW; generate / status transitions need
 * M44_INVOICE_MANAGE (SoD). Invoice numbers are unique + auditable (BR-08).
 */
export function InvoiceManagementScreen(): ReactNode {
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [billingId, setBillingId] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (typeFilter) params.set('invoiceType', typeFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (search.trim()) params.set('search', search.trim());
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: InvoiceRow[]; total: number }>(`/v1/billing/subscription-invoices?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, typeFilter, statusFilter, search, page, reload]);

  const generate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!billingId.trim()) { setActionError('A billing transaction id is required.'); return; }
    try {
      await apiRequest('/v1/billing/subscription-invoices/generate', {
        method: 'POST',
        body: { billingId: billingId.trim() },
        token,
      });
      setNotice('Invoice generated.');
      setBillingId('');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError('You are not authorised to manage invoices (M44_INVOICE_MANAGE required).');
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const setStatus = async (id: string, status: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/billing/subscription-invoices/${id}/status`, { method: 'POST', body: { status }, token });
      setNotice(`Invoice ${status}.`);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError('You are not authorised for this action (M44_INVOICE_MANAGE required).');
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="invoice-management-screen">
      <h1>Invoice Management</h1>

      <section aria-label="invoice-generate">
        <h2>Generate Invoice</h2>
        <form aria-label="invoice-form" onSubmit={(e) => void generate(e)}>
          <RecordPicker<{ billingId: string; billingCode: string }>
            endpoint="/v1/billing/bills?pageSize=500"
            idField="billingId"
            label="Billing Transaction"
            testId="field-billing-id"
            value={billingId}
            optionLabel={(b) => b.billingCode}
            onPick={setBillingId}
            emptyText="No billing transactions yet"
          />
          <button type="submit" aria-label="submit-invoice">Generate</button>
        </form>
        {actionError && <p role="alert" aria-label="invoice-action-error">{actionError}</p>}
        {notice && <p aria-label="invoice-notice">{notice}</p>}
      </section>

      <section aria-label="invoice-filters">
        <label>Type
          <select aria-label="filter-type" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {INVOICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label>Status
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {INVOICE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>Search<input aria-label="filter-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} /></label>
      </section>

      {error && <p role="alert" aria-label="invoice-error">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <>
          <p aria-label="invoice-total">{total} invoice(s).</p>
          <table aria-label="invoice-table">
            <thead>
              <tr><th scope="col">Number</th><th scope="col">Customer</th><th scope="col">Type</th><th scope="col">Total</th><th scope="col">Balance</th><th scope="col">Status</th><th scope="col">Actions</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7}>No invoices.</td></tr>
              ) : rows.map((i) => (
                <tr key={i.invoiceId} data-invoice={i.invoiceId}>
                  <td aria-label={`invoice-number-${i.invoiceId}`}>{i.invoiceNumber}</td>
                  <td>{personLabel(i.saasTenantName, i.saasTenantCode) ?? '—'}</td>
                  <td>{i.invoiceType}</td>
                  <td>{i.currency} {money(Number(i.totalAmount))}</td>
                  <td>{i.currency} {money(Number(i.balanceAmount))}</td>
                  <td aria-label={`invoice-status-${i.invoiceId}`}>{i.status}</td>
                  <td>
                    <button type="button" aria-label={`issue-${i.invoiceId}`} onClick={() => void setStatus(i.invoiceId, 'ISSUED')}>Issue</button>
                    <button type="button" aria-label={`cancel-${i.invoiceId}`} onClick={() => void setStatus(i.invoiceId, 'CANCELLED')}>Cancel</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="invoice-pagination">
            <button type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
            <span aria-label="page-indicator">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
            <button type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
          </nav>
        </>
      )}
    </main>
  );
}
